/**
 * Out of credits, in the thread (W4 §1): a turn or a tool refused for credits says so where the
 * user's message is, with the one purchase that covers the need. Org members, whose org wallet pays,
 * are pointed at us instead of a purchase.
 */

import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { billingAPI, type CreditPack, type WalletBalance } from '../../services/api';
import type { PaywallInfo } from '../../services/billingPaywallDetector';
import { getVideoEstimate } from './videoEstimate';
import { EnterpriseContactDialog } from './EnterpriseContactDialog';
import { QuestionButton, QuestionCard } from '../studio/plan/Thread';
import { studioBOn } from '../../lib/studioB/flag';

/** The smallest pack that covers `need` credits, else the largest. */
function smallestCoveringPack(packs: CreditPack[], need: number): CreditPack | null {
  const sorted = packs.filter((p) => p.credits > 0).sort((a, b) => a.credits - b.credits);
  return sorted.find((p) => p.credits >= need) ?? sorted.at(-1) ?? null;
}

function actionText(action?: string): string {
  return !action || action.startsWith('/') || action === 'chat turn' ? 'keep going' : action;
}

const asCredits = (milli: number) => Number((Math.max(0, milli) / 1000).toFixed(2));

export function OutOfCreditsCard({ paywall }: { paywall: PaywallInfo }) {
  const [wallet, setWallet] = useState<WalletBalance | null>(null);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [estimatedNeedMilli, setEstimatedNeedMilli] = useState<number | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    billingAPI.getBalance().then((b) => { if (!cancelled) setWallet(b); }).catch(() => {});
    billingAPI.getPacks().then((r) => { if (!cancelled) setPacks(r.packs ?? []); }).catch(() => {});
    if (paywall.needMilli == null) {
      getVideoEstimate().then((e) => { if (!cancelled && e) setEstimatedNeedMilli(e.highMilli); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [paywall.needMilli]);

  const needMilli = paywall.needMilli ?? estimatedNeedMilli;
  const availableMilli = paywall.availableMilli ?? wallet?.availableMilli ?? null;
  const short = needMilli != null && availableMilli != null ? Math.ceil((needMilli - Math.max(0, availableMilli)) / 1000) : null;
  const org = wallet?.scope === 'org';
  const pack = smallestCoveringPack(packs, short ?? 0);

  async function buy(p: CreditPack) {
    setRedirecting(true);
    setError(null);
    try {
      const { url } = await billingAPI.startCheckout({ pack: p.id });
      window.location.href = url;
    } catch {
      setError('Could not start checkout. Please try again.');
      setRedirecting(false);
    }
  }

  const need = short != null && short > 0
    ? `You need about ${short} more credits to ${actionText(paywall.action)}.`
    : `You need more credits to ${actionText(paywall.action)}.`;
  const have = `${availableMilli != null ? `You have ${asCredits(availableMilli)}. ` : ''}Your script, voice and look are saved, and I'll pick up exactly here.`;

  if (studioBOn()) {
    return (
      <QuestionCard title={need} facts={have} hint={error ?? undefined}>
        {org ? (
          <QuestionButton label="Contact us" recommended onClick={() => setContactOpen(true)} />
        ) : (
          <>
            {pack && <QuestionButton label={`Buy ${pack.credits} credits`} recommended disabled={redirecting} onClick={() => buy(pack)} />}
            <Link to="/account/billing" className="inline-flex h-9 items-center rounded-[var(--b-radius-control)] bg-b-raised px-3.5 text-b-control text-b-t1 no-underline hover:bg-b-elevated">
              See all packs
            </Link>
          </>
        )}
        <EnterpriseContactDialog open={contactOpen} onOpenChange={setContactOpen} source="out_of_credits" />
      </QuestionCard>
    );
  }

  return (
    <div className="my-1 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-[13px]">
      <p className="font-medium">
        {short != null && short > 0
          ? `You need about ${short} more credits to ${actionText(paywall.action)}.`
          : `You need more credits to ${actionText(paywall.action)}.`}
      </p>
      <p className="mt-1 text-muted-foreground">
        {availableMilli != null && `You have ${asCredits(availableMilli)}. `}
        Your script, voice and look are saved, and I'll pick up exactly here.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {org ? (
          <button
            type="button"
            onClick={() => setContactOpen(true)}
            className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            Contact us
          </button>
        ) : (
          <>
            {pack && (
              <button
                type="button"
                disabled={redirecting}
                onClick={() => buy(pack)}
                className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                Buy {pack.credits} credits
              </button>
            )}
            <Link to="/account/billing" className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted">
              See all packs
            </Link>
          </>
        )}
      </div>
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
      <EnterpriseContactDialog open={contactOpen} onOpenChange={setContactOpen} source="out_of_credits" />
    </div>
  );
}
